import { DATASTREAM_COLOR_WIDGET_TYPES } from '../layout/webDashboardConstants';

const PERIOD_OPTION_DEFAULTS = [
	{ label: 'Daily', value: 'day' },
	{ label: 'Weekly', value: 'week' },
	{ label: 'Monthly', value: 'month' },
];

const PERIOD_OPTION_ALLOWED_VALUES = new Set(PERIOD_OPTION_DEFAULTS.map((option) => option.value));

export const buildPeriodOptions = (translatedPeriodOptions) => {
	const translated = Array.isArray(translatedPeriodOptions) ? translatedPeriodOptions : [];
	const merged = new Map(PERIOD_OPTION_DEFAULTS.map((option) => [option.value, option]));

	translated.forEach((option) => {
		const normalizedValue = String(option?.value || '').trim().toLowerCase();
		if (!PERIOD_OPTION_ALLOWED_VALUES.has(normalizedValue)) return;
		merged.set(normalizedValue, {
			label: option?.label || merged.get(normalizedValue)?.label || normalizedValue,
			value: normalizedValue,
		});
	});

	return Array.from(merged.values()).filter((option) => PERIOD_OPTION_ALLOWED_VALUES.has(option.value));
};

export const filterWidgetOptionsByQuery = (widgetOptions, searchQuery) => {
	const query = String(searchQuery || '').trim().toLowerCase();
	if (!query) return widgetOptions;

	return widgetOptions.filter((widgetOption) => {
		const props = widgetOption?.props || {};
		return [props.title, props.type, props.id]
			.filter(Boolean)
			.some((value) => String(value).toLowerCase().includes(query));
	});
};

export const hasWidgetColorSettings = (props) => {
	const safeProps = props || {};
	if (Object.keys(safeProps).length > 0) return true;
	const propsColorKeys = Object.keys(safeProps).some((key) =>
		key.toLowerCase().includes('color')
	);
	const curvesColorKeys = Array.isArray(safeProps.curves)
		&& safeProps.curves.some((curve) =>
			Object.keys(curve || {}).some((key) => key.toLowerCase().includes('color'))
		);
	const stopsColorKeys = Array.isArray(safeProps.colors)
		&& safeProps.colors.some((stop) =>
			Object.keys(stop || {}).some((key) => key.toLowerCase().includes('color'))
		);

	return propsColorKeys || curvesColorKeys || stopsColorKeys;
};

const FONT_SIZE_FIELD_LABELS = {
	labelFontSize: 'Title Font Size',
	valueFontSize: 'Value Font Size',
	titleFontSize: 'Title Font Size',
	assetNameFontSize: 'Asset Name Font Size',
	datastreamLabelFontSize: 'Datastream Label Font Size',
	titleSize: 'Title Font Size',
	datastreamTitleSize: 'Datastream Title Font Size',
	valueSize: 'Value Font Size',
	dayLabelFontSize: 'Days Label Font Size',
	statusFontSize: 'Status Font Size',
	switchSize: 'Switch Button Size',
	buttonSize: 'Button Size',
	keyFontSize: 'Key Font Size',
};

const FONT_SIZE_FIELDS_BY_TYPE = {
	'asset-status': ['titleFontSize', 'assetNameFontSize', 'datastreamLabelFontSize', 'valueFontSize'],
	'environmental-status': ['titleSize', 'datastreamTitleSize', 'valueSize'],
	'time-range': ['titleSize', 'valueSize', 'dayLabelFontSize'],
	pipe: ['titleFontSize', 'statusFontSize'],
	elbow: ['titleFontSize', 'statusFontSize'],
	switch: ['labelFontSize', 'switchSize'],
	'push-button': ['labelFontSize', 'valueFontSize', 'buttonSize'],
	keypad: ['labelFontSize', 'valueFontSize', 'keyFontSize'],
	'image-button': ['labelFontSize'],
	'water-pump': ['labelFontSize'],
	'text-title': [],
	'status-arrow': [],
};

export const buildFontSizeFields = (settingsLabels = {}, widgetType = '') => {
	const keys = FONT_SIZE_FIELDS_BY_TYPE[widgetType] || ['labelFontSize', 'valueFontSize'];
	return keys.map((key) => ({
		key,
		label: settingsLabels?.[key] || FONT_SIZE_FIELD_LABELS[key] || key,
	}));
};

export const getSafeDatastreamList = (datastreams) => (datastreams || []).filter(ds => ds);

export const getSelectedDatastreamForEdit = ({
	rawId,
	rawPin,
	validDatastreams,
	deviceDatastream,
	findDatastreamByReference,
}) => {
	const fromTemplate = findDatastreamByReference(validDatastreams, rawId, rawPin);
	if (fromTemplate) return fromTemplate;
	const fromDevice = findDatastreamByReference(deviceDatastream, rawId, rawPin);
	return fromDevice || null;
};

export const getDeviceTemplateSignature = (device) => {
	if (!device) return '';
	const tidValue = device?.template?.tid;
	if (tidValue !== null && tidValue !== undefined && String(tidValue).trim() !== '') {
		return `tid:${String(tidValue)}`;
	}
	const templateIdValue = device?.template_id ?? device?.template?.id;
	if (templateIdValue === null || templateIdValue === undefined || String(templateIdValue).trim() === '') {
		return '';
	}
	return `id:${String(templateIdValue)}`;
};

export const stripDatastreamManagedProps = (props, widgetType, cloneDashboardValue) => {
	const cloneValue = typeof cloneDashboardValue === 'function'
		? cloneDashboardValue
		: (value) => JSON.parse(JSON.stringify(value || {}));
	const nextProps = cloneValue(props || {});
	if (!nextProps || typeof nextProps !== 'object' || Array.isArray(nextProps)) {
		return nextProps;
	}

	delete nextProps.min;
	delete nextProps.max;
	delete nextProps.unit;

	return nextProps;
};

const DATASTREAM_WIDGET_COLOR_KEYS = {
	gauge: ['color', 'startColor', 'stopColor'],
	'radial-gauge': ['color', 'startColor', 'stopColor'],
	liquid: ['color'],
	'water-tank': ['color'],
	label: ['color'],
	led: ['color'],
	number: ['color'],
	slider: ['color'],
	switch: ['color'],
	'push-button': ['color'],
	'segment-switch': ['activeBackgroundColor', 'activeOptionColor'],
	'status-arrow': ['color'],
	'text-title': ['color'],
};

const DEFAULT_DATASTREAM_FALLBACK_COLORS = {
	gauge: {
		color: ['#ff928a'],
		startColor: ['#ff928a'],
		stopColor: ['#ff928a'],
	},
	'radial-gauge': {
		color: ['#ff928a'],
		startColor: ['#ff928a'],
		stopColor: ['#ff928a'],
	},
	liquid: {
		color: ['#74c7ff'],
	},
	'water-tank': {
		color: ['#72b8f4'],
	},
	label: {
		color: ['#ff00ff'],
	},
	led: {
		color: ['#00f0f0'],
	},
	number: {
		color: ['#3ccfe6'],
	},
	slider: {
		color: ['#f337b4', '#4489b6'],
	},
	switch: {
		color: ['#c52184'],
	},
	'push-button': {
		color: ['#4fc4d3'],
	},
	'segment-switch': {
		activeBackgroundColor: ['rgba(60,159,181,0.82)', 'rgba(60, 159, 181, 0.82)'],
		activeOptionColor: ['rgba(60,159,181,0.82)', 'rgba(60, 159, 181, 0.82)'],
	},
	'status-arrow': {
		color: ['#26b4ff'],
	},
	'text-title': {
		color: ['#00f0f0'],
	},
};

const normalizeColorToken = (value) => String(value || '').replace(/\s+/g, '').trim().toLowerCase();

const isMissingOrDefaultColorValue = (widgetType, key, value) => {
	if (value === undefined || value === null) return true;
	if (typeof value === 'string' && value.trim() === '') return true;

	const baseKey = key.endsWith('Light') ? key.slice(0, -5) : key;
	const defaultColors = DEFAULT_DATASTREAM_FALLBACK_COLORS[widgetType]?.[baseKey] || [];
	const normalized = normalizeColorToken(value);
	return defaultColors.some((defaultColor) => normalizeColorToken(defaultColor) === normalized);
};

export const applyDatastreamDefaultColors = (args, legacyWidgetType, legacyDatastream, legacyThemeColorKey) => {
	const isObjectArgs = args && typeof args === 'object' && Object.prototype.hasOwnProperty.call(args, 'props');
	const props = isObjectArgs ? args.props : args;
	const widgetType = isObjectArgs ? args.widgetType : legacyWidgetType;
	const datastream = isObjectArgs ? args.datastream : legacyDatastream;
	const themeColorKey = isObjectArgs ? args.themeColorKey : legacyThemeColorKey;

	if (!datastream?.color || !DATASTREAM_COLOR_WIDGET_TYPES.includes(widgetType)) {
		return props;
	}

	let nextProps = props;
	const colorKeys = DATASTREAM_WIDGET_COLOR_KEYS[widgetType] || ['color'];
	const getThemeColorKey = typeof themeColorKey === 'function' ? themeColorKey : (key) => `${key}Light`;
	colorKeys.forEach((key) => {
		const themeKey = getThemeColorKey(key);
		const lightKey = `${key}Light`;
		const candidateKeys = Array.from(new Set([key, themeKey, lightKey]));

		candidateKeys.forEach((candidateKey) => {
			if (isMissingOrDefaultColorValue(widgetType, candidateKey, props?.[candidateKey])) {
				if (nextProps === props) nextProps = { ...props };
				nextProps[candidateKey] = datastream.color;
			}
		});
	});

	return nextProps;
};

export const formatPinForTitle = (pin) => {
	if (!pin) return '';
	const match = String(pin).match(/^([A-Za-z]+)\s*(\d+)$/);
	return match ? `[${match[1]}${match[2]}]` : `[${pin}]`;
};

export const preserveTitleSpaces = (text) => String(text ?? '').replace(/ /g, '\u00A0');

export const toLengthInputNumber = (value, fallback = 0) => {
	if (value === null || value === undefined || value === '') return fallback;
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	const normalized = String(value).replace('%', '').replace('px', '').trim();
	const parsed = Number(normalized);
	return Number.isFinite(parsed) ? parsed : fallback;
};

export const roundGeometryValue = (value) => Number(Number(value).toFixed(4));

export const formatGeometryDraftValue = (value) => String(roundGeometryValue(value));

export const getEditContainingBackground = ({
	editWidget,
	editWidgetRectPx,
	widgets,
	getContainingBackgroundImage,
}) => {
	if (!editWidget) return null;
	if (editWidget.type === 'background-image' || editWidget.type === 'box-widget' || editWidget.type === 'camera-stream') return null;
	if (!editWidgetRectPx) return null;
	return getContainingBackgroundImage(
		editWidgetRectPx.left,
		editWidgetRectPx.top,
		widgets || [],
		editWidget.id,
	);
};

export const parseDimensionNumber = (value) => {
	if (typeof value === 'number' && Number.isFinite(value)) return value;
	if (typeof value === 'string') {
		const parsed = parseFloat(value);
		return Number.isFinite(parsed) ? parsed : null;
	}
	return null;
};

export const normalizeLegacyWidgetDimensions = ({
	widgets,
	widgetType,
	legacyWidth,
	legacyHeight,
	normalizedWidth,
	normalizedHeight,
}) => {
	let changed = false;
	const normalizedWidgets = (Array.isArray(widgets) ? widgets : []).map((widget) => {
		if (widget?.type !== widgetType) {
			return widget;
		}

		const currentWidth = parseDimensionNumber(widget?.width);
		const currentHeight = parseDimensionNumber(widget?.height);
		if (
			currentWidth == null ||
			currentHeight == null ||
			Math.abs(currentWidth - legacyWidth) > 0.5 ||
			Math.abs(currentHeight - legacyHeight) > 0.5
		) {
			return widget;
		}

		changed = true;
		return {
			...widget,
			width: normalizedWidth,
			height: normalizedHeight,
		};
	});

	return {
		changed,
		widgets: normalizedWidgets,
	};
};
